'use client';

import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth-context';
import { Card } from '@/components/ds/Card';

export default function DashboardPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const whoamiQuery = trpc.system.whoami.useQuery();
  const currentBusinessQuery = trpc.system.currentBusiness.useQuery();

  return (
    <div style={{ padding: '1.5rem' }}>
      {/*
       * CRITICAL: this <h1> text must remain exactly 'לוח בקרה'.
       * The e2e login test asserts getByRole('heading', { name: 'לוח בקרה' }).
       * The TopBar shows the same text as a <div> (not a heading) to avoid
       * a duplicate-heading Playwright strict-mode violation.
       */}
      <h1
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-title1)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBlockEnd: '1.5rem',
          lineHeight: 'var(--lh-title1)',
        }}
      >
        {t('dashboard.title')}
      </h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
          maxWidth: 960,
        }}
      >
        {/* Welcome card */}
        <Card padding={24}>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-secondary)',
              margin: 0,
            }}
          >
            {t('dashboard.welcome')}, {auth.user?.phone}
          </p>
        </Card>

        {/* whoami debug card */}
        <Card padding={24}>
          <section aria-label="whoami">
            <h2
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-label)',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBlockEnd: '0.5rem',
                margin: 0,
              }}
            >
              whoami
            </h2>
            {whoamiQuery.isPending && (
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--text-tertiary)',
                  marginBlockStart: '0.5rem',
                }}
              >
                {t('common.loading')}
              </p>
            )}
            {whoamiQuery.error && (
              <p
                role="alert"
                style={{
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--error)',
                  marginBlockStart: '0.5rem',
                }}
              >
                {t('common.error')}: {whoamiQuery.error.message}
              </p>
            )}
            {whoamiQuery.data && (
              <pre
                style={{
                  background: 'var(--bg-sunken)',
                  padding: '0.75rem',
                  borderRadius: 'var(--r-md)',
                  fontSize: 12,
                  overflow: 'auto',
                  marginBlockStart: '0.5rem',
                }}
              >
                {JSON.stringify(whoamiQuery.data, null, 2)}
              </pre>
            )}
          </section>
        </Card>

        {/* Current business debug card */}
        <Card padding={24}>
          <section aria-label="current-business">
            <h2
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-label)',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBlockEnd: '0.5rem',
                margin: 0,
              }}
            >
              {auth.activeBusiness?.businessName ?? '-'}
            </h2>
            {currentBusinessQuery.isPending && (
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--text-tertiary)',
                  marginBlockStart: '0.5rem',
                }}
              >
                {t('common.loading')}
              </p>
            )}
            {currentBusinessQuery.error && (
              <p
                role="alert"
                style={{
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--error)',
                  marginBlockStart: '0.5rem',
                }}
              >
                {t('common.error')}: {currentBusinessQuery.error.message}
              </p>
            )}
            {currentBusinessQuery.data && (
              <pre
                style={{
                  background: 'var(--bg-sunken)',
                  padding: '0.75rem',
                  borderRadius: 'var(--r-md)',
                  fontSize: 12,
                  overflow: 'auto',
                  marginBlockStart: '0.5rem',
                }}
              >
                {JSON.stringify(currentBusinessQuery.data, null, 2)}
              </pre>
            )}
          </section>
        </Card>
      </div>
    </div>
  );
}
